/* global window, document */
import * as cx from 'classnames';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { hasAds } from 'mk/bazaar/common/userUtils';
import { AdSlot, OldFluxibleContext } from './AdSlot';

interface OwnProps {
    context: OldFluxibleContext;
    zoneName: string;
    className: string;

    // how mamy pixel bellow top edge of browser window should be the AdSlot
    topFixedPos?: number;

    // signals that change to this prop might cause reflow (resize)
    // of bounding box. This component has to adapt by recomputing
    // its sticky class with updatePosition()
    reflowTrigger?: any;

    // use if there's no need to initial registering of the ad slot into the props.context.oxZones
    mountWithoutRegistering?: boolean;
}

interface OwnState {
    adSlotStyle: {
        position: 'absolute' | 'fixed';
        visibility?: 'hidden' | 'visible';
        top?: number;
        left?: number;
        right?: number;
    };
}

/**
 * Ad slot, where banner is floating (sticky on screen) and OUTSIDE of main page content (off-site).
 *
 * Banner position must be always 'fixed', to avoid creation of horizontal scrollbars by the browser.
 */
export class AdSlotOffsite extends React.Component<OwnProps, OwnState> {

    public static defaultProps: Partial<OwnProps> = {
        topFixedPos: 10,
    };

    private updatePositionTrigger: () => void;

    private adSlot: HTMLDivElement;
    private container: HTMLDivElement;

    private static scrollTop() {
        return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }

    // get Top/Bottom position of element according to browser viewport
    private static positionInViewport(elem) {
        const box = elem.getBoundingClientRect();
        const body = document.body;
        const docElem = document.documentElement;

        const scrollTop = window.pageYOffset || docElem.scrollTop || body.scrollTop;
        const scrollLeft = window.pageXOffset || docElem.scrollLeft || body.scrollLeft;

        const clientTop = docElem.clientTop || body.clientTop || 0;
        const clientLeft = docElem.clientLeft || body.clientLeft || 0;

        const top = box.top + scrollTop - clientTop;
        const bottom = box.bottom + scrollTop - clientTop;
        const left = box.left + scrollLeft - clientLeft;

        return {
            top: Math.round(top),
            bottom: Math.round(bottom),
            left: Math.round(left),
        };
    }

    constructor(props) {
        super(props);
        this.state = {adSlotStyle: {
            // 11.1.2017
            // CSS3 uz ma position:'sticky', ktore riesi ten isty usecase
            // ako AdSlotOffsite.
            // Browsery ho ale este nepodporuju, preto to iste simulujeme cez
            // positin:'fixed' a javascript
            position: 'fixed',

            // aby nebolo vidno ako element skoci hore,
            // ak je stranka pri page-reload odscrollovana
            visibility: 'hidden',
        }};
        this.updatePositionTrigger = () => { this.updatePosition(); };
    }

    public render() {
        if (!hasAds(this.props.context.requestUser)) {
            return null;
        }

        return (
            <div className="ad-offsite-container" ref={(r) => this.container = r}>
                <AdSlot
                    slotRef={this.setAdSlotRef}
                    context={this.props.context}
                    zoneName={this.props.zoneName}
                    className={cx(this.props.className, this.state.adSlotStyle.position)}
                    style={this.state.adSlotStyle}
                    htmlData={{
                        'component': 'AdSlotOffsite',
                        'zone-name': `${this.props.zoneName}`,
                    }}
                    mountWithoutRegistering={this.props.mountWithoutRegistering}
                />
            </div>
        );
    }

    public componentDidMount() {
        this.updatePosition();

        // Update position of AdSlot if this container has been scrolled
        // or browser window has been resized
        window.addEventListener('scroll', this.updatePositionTrigger);
        window.addEventListener('resize', this.updatePositionTrigger);
    }

    public componentWillUnmount() {
        window.removeEventListener('scroll', this.updatePositionTrigger);
        window.removeEventListener('resize', this.updatePositionTrigger);
    }

    public componentWillReceiveProps() {
        // Recompute position of AdSlot. Called if reflowTrigger has changed.
        this.updatePosition();
    }

    private setAdSlotRef = (r: HTMLDivElement) => this.adSlot = r;

    private updatePosition() {
        if (!this.container || !this.adSlot) {
            return;
        }

        const cbox = AdSlotOffsite.positionInViewport(this.container);
        const zbox = AdSlotOffsite.positionInViewport(this.adSlot);

        const scrollTop = AdSlotOffsite.scrollTop();
        // position of the element if it would be set to 'position: fixed' now
        const fixedTop = scrollTop + this.props.topFixedPos;

        // console.log('cbox', cbox);
        // console.log('zbox', zbox);
        // console.log('scrollTop', scrollTop);
        // console.log('fixedTop', fixedTop);

        const ztop =
            fixedTop <= cbox.top ?
                // top of zbox would be above cbox, so attach to top
                cbox.top - scrollTop :
                fixedTop + (zbox.bottom - zbox.top) >= cbox.bottom ?
                    // bottom of zbox would be below cbox, so attach to bottom
                    cbox.bottom - (zbox.bottom - zbox.top) - scrollTop :
                    // keep zbox inside of cbox, 10px (topFixedPos px) below top
                    // of browser window
                    this.props.topFixedPos;

        // console.log('new sticky position', ztop);
        this.setState({
            adSlotStyle: {
                position: 'fixed',
                left: cbox.left,
                top: ztop,
            },
        });
    }
}
